import type { PartTableDefinition } from "@project/emerge";
import { LTPartType } from "../enum/PartType";

function formatNumber(n: number): string {
  return n % 1 === 0 ? n.toFixed(0) : n.toFixed(1);
}

function isVector2Like(value: unknown): value is { x: number; y: number } {
  return (
    typeof value === "object" &&
    value !== null &&
    "x" in value &&
    "y" in value &&
    typeof (value as { x: unknown }).x === "number" &&
    typeof (value as { y: unknown }).y === "number"
  );
}

interface PointWithDistance {
  distanceTo(other: PointWithDistance): number;
}

function joinSegmentLengths(points: PointWithDistance[]) {
  if (!Array.isArray(points) || points.length < 2) return "";
  const segments: number[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (!a || !b) continue;
    const d = a.distanceTo(b);
    const value = Math.floor(10 * d + 1e-6) / 10;
    segments.push(value);
  }
  return segments.join(" - ");
}

export const tableRenderers: Record<string, PartTableDefinition> = {
  [LTPartType.Panel]: {
    headers: ["Breite", "Höhe", "Stärke", "Material"],
    getValues: (value: unknown): Array<string | number> => {
      if (
        typeof value === "object" &&
        value !== null &&
        "size" in value &&
        isVector2Like((value as { size: unknown }).size)
      ) {
        const v = value as {
          size: { x: number; y: number };
          strength?: number;
          color?: { name?: string };
          material?: { strength?: number };
        };
        const width = formatNumber(v.size.x);
        const height = formatNumber(v.size.y);
        const strength: string | number = v.strength ?? "";
        const materialName = v.color?.name ?? "";
        return [width, height, strength, materialName];
      }
      return ["", "", "", ""];
    },
  },
  [LTPartType.TrayPanel]: {
    headers: ["Breite", "Höhe", "Stärke", "Material"],
    getValues: (value: unknown): Array<string | number> => {
      if (
        typeof value === "object" &&
        value !== null &&
        "size" in value &&
        isVector2Like((value as { size: unknown }).size)
      ) {
        const v = value as {
          size: { x: number; y: number };
          strength?: number;
          color?: { name?: string };
          material?: { strength?: number };
        };
        const width = formatNumber(v.size.x);
        const height = formatNumber(v.size.y);
        const strength: string | number = v.strength ?? "";
        const materialName = v.color?.name ?? "";
        return [width, height, strength, materialName];
      }
      return ["", "", "", ""];
    },
  },
  [LTPartType.Profile]: {
    headers: ["Art", "Länge", "Seite"],
    getValues: (value: unknown): Array<string | number> => {
      const v = value as {
        partSelection: { name: string };
        inverse?: boolean;
        partSelection2?: unknown;
        cutReference?: unknown;
      } & {
        points: PointWithDistance[];
        partSelection: { inverted?: boolean } & { name: string } & {
          gapWidth?: number;
          height?: number;
          strength?: number;
          extra?: number;
          description?: string;
        };
      };
      const name = v?.partSelection?.name ?? "";
      const length = joinSegmentLengths(v?.points);
      const side = v?.inverse !== v?.partSelection?.inverted ? "M" : "F";
      return [name, length, side];
    },
  },
  [LTPartType.AngleSection]: {
    headers: ["Typ", "Länge", "Abstand", ""],
    getValues: (value: unknown): Array<string | number> => {
      const v = value as {
        partSelection: { name: string; cutSize?: number };
        length: number;
      } & {
        minimumOffset?: number;
        cuts?: {
          left: { top?: boolean; bottom?: boolean };
          right: { top?: boolean; bottom?: boolean };
        };
      };
      const type = v?.partSelection?.name ?? "";
      const len = v?.length ?? 0;
      const rivetDistance = 150;
      const minimumOffset = v?.minimumOffset ?? 50;
      const minimumLengthForRivet = 2 * minimumOffset;
      let offset: number | null;
      if (len < minimumLengthForRivet) offset = 0;
      else if (len <= rivetDistance + minimumLengthForRivet) offset = len / 2;
      else {
        offset = (len % rivetDistance) / 2;
        while (offset < minimumOffset) {
          offset += 0.5 * rivetDistance;
        }
      }
      let sign = "";
      const c = v?.cuts;
      if (c) {
        if (c.left.top && c.right.top) sign = "/ \\";
        if (c.left.bottom && c.right.bottom) sign = "/ \\";
        if (c.left.top && c.left.bottom) sign = "v";
        if (c.right.top && c.right.bottom) sign = sign === "v" ? "< >" : "v";
      }
      return [type, len, offset ?? 0, sign];
    },
  },
  [LTPartType.Corner]: {
    headers: ["Typ", "Beschreibung"],
    getValues: (value: unknown): Array<string | number> => {
      const v = value as {
        partSelection: { name: string; description: string };
      };
      const type = v?.partSelection?.name ?? "";
      const desc = v?.partSelection?.description ?? "";
      return [type, desc];
    },
  },
  [LTPartType.CornerBrace]: {
    headers: ["Typ", "Beschreibung"],
    getValues: (value: unknown): Array<string | number> => {
      const v = value as {
        partSelection: { name: string; description: string };
      };
      return [
        v?.partSelection?.name ?? "",
        v?.partSelection?.description ?? "",
      ];
    },
  },
  [LTPartType.RackRail]: {
    headers: ["Länge", "Symbol"],
    getValues: (value: unknown): Array<string | number> => {
      const v = value as {
        points: PointWithDistance[];
        partSelection: { symbol: string };
      };
      const length =
        Array.isArray(v?.points) && v.points[0] && v.points[1]
          ? formatNumber(v.points[0].distanceTo(v.points[1]))
          : "";
      return [length, v?.partSelection?.symbol ?? ""];
    },
  },
  [LTPartType.Volume]: {
    headers: ["Breite", "Tiefe", "Höhe"],
    getValues: (value: unknown): Array<string | number> => {
      const v = value as { size: [number, number, number] };
      return Array.isArray(v?.size)
        ? [v.size[0], v.size[2], v.size[1]]
        : ["", "", ""];
    },
  },
  [LTPartType.SlideRail]: {
    headers: ["Typ", "Beschreibung", "Breite", "Länge"],
    getValues: (value: unknown): Array<string | number> => {
      const v = value as {
        partSelection?: { name?: string; description?: string };
        size?: { x?: number; y?: number };
      };
      const type = v?.partSelection?.name ?? "";
      const description = v?.partSelection?.description ?? "";
      const width = v?.size?.y ? formatNumber(v.size.y) : "";
      const length = v?.size?.x ? formatNumber(v.size.x) : "";
      return [type, description, width, length];
    },
  },
};

// -------- Klavierband: Typ, Beschreibung, Segmente --------
tableRenderers[LTPartType.PianoHinge] = {
  headers: ["Typ", "Beschreibung", "Segmente"],
  getValues: (value: unknown): Array<string | number> => {
    const v = value as {
      partSelection?: { name?: string; description?: string };
      segments?: number;
    };
    const type = v?.partSelection?.name ?? "";
    const description = v?.partSelection?.description ?? "";
    const segments = typeof v?.segments === "number" ? v.segments : "";
    return [type, description, segments];
  },
};

// -------- Standard-Renderer für Anbauteile (Typ + Beschreibung) --------
const standardSupplyRenderer: PartTableDefinition = {
  headers: ["Typ", "Beschreibung"],
  getValues: (value: unknown): Array<string | number> => {
    const v = value as {
      partSelection?: { name?: string; description?: string };
    };
    return [v?.partSelection?.name ?? "", v?.partSelection?.description ?? ""];
  },
};

// Mehrere Typen auf denselben Standard-Renderer abbilden (keine Duplikate)
tableRenderers[LTPartType.Dish] = standardSupplyRenderer;
tableRenderers[LTPartType.Handle] = standardSupplyRenderer;
tableRenderers[LTPartType.Foot] = standardSupplyRenderer;
tableRenderers[LTPartType.TrolleyHandle] = standardSupplyRenderer;
tableRenderers[LTPartType.Lock] = standardSupplyRenderer;
tableRenderers[LTPartType.Hinge] = standardSupplyRenderer;
tableRenderers[LTPartType.Pad] = standardSupplyRenderer;
tableRenderers[LTPartType.Roller] = standardSupplyRenderer;
tableRenderers[LTPartType.SlideRail] = standardSupplyRenderer;
